import * as Api from '@ViewModels';

type TRecipientCountKey = 'attending' | 'notAttending' | 'noResponse' | 'checkedIn';

const statusCountKeys: Partial<Record<Api.EventRegistrationResponseStatus, TRecipientCountKey>> = {
	[Api.EventRegistrationResponseStatus.Attending]: 'attending',
	[Api.EventRegistrationResponseStatus.NotAttending]: 'notAttending',
	[Api.EventRegistrationResponseStatus.NoResponse]: 'noResponse',
	[Api.EventRegistrationResponseStatus.CheckedIn]: 'checkedIn',
};

type TSurveyWithStats = { stats?: Api.IEventRegistrationSurveyStats } | undefined;

export const getKnownContactStats = (survey: TSurveyWithStats) =>
	survey?.stats?.responseStats?.find(x => x.category === Api.EventRegistrationSurveyResponseStatCategory.KnownContacts);

/** Registrant contacts for a status, plus guest contacts when guests are included. Guests can overlap registrants. */
export const getFollowUpRecipientCount = (
	survey: TSurveyWithStats,
	status: Api.EventRegistrationResponseStatus | null,
	includeGuests: boolean
) => {
	const stats = getKnownContactStats(survey);
	const guestStats = includeGuests ? survey?.stats?.guestContacts : null;
	if (status === null) {
		return (stats?.responses || 0) + (guestStats?.total || 0);
	}

	const key = statusCountKeys[status];
	return key ? (stats?.[key] || 0) + (guestStats?.[key] || 0) : 0;
};

const statusOptionLabels: Partial<Record<Api.EventRegistrationResponseStatus, [string, string]>> = {
	[Api.EventRegistrationResponseStatus.Attending]: ['attending', 'Attending'],
	[Api.EventRegistrationResponseStatus.CheckedIn]: ['checkedIn', 'Checked in'],
	[Api.EventRegistrationResponseStatus.NotAttending]: ['notAttending', 'Not attending'],
	[Api.EventRegistrationResponseStatus.NoResponse]: ['noResponse', 'No response'],
};

export const buildFollowUpStatusOption = (
	survey: TSurveyWithStats,
	status: Api.EventRegistrationResponseStatus,
	includeGuests: boolean
) => {
	const [id, text] = statusOptionLabels[status] ?? statusOptionLabels[Api.EventRegistrationResponseStatus.NoResponse];
	const count = getFollowUpRecipientCount(survey, status, includeGuests);
	return { count, disabled: !count, id: `${id}-${count}`, representedObject: status, text };
};

export const buildAllRecipientsOption = (survey: TSurveyWithStats, includeGuests: boolean) => ({
	count: getFollowUpRecipientCount(survey, null, includeGuests),
	id: 'all',
	representedObject: null as Api.EventRegistrationResponseStatus | null,
	text: 'All recipients',
});

type TFollowUpOption = ReturnType<typeof buildAllRecipientsOption> & { disabled?: boolean };

const allFollowUpStatuses = [
	Api.EventRegistrationResponseStatus.Attending,
	Api.EventRegistrationResponseStatus.CheckedIn,
	Api.EventRegistrationResponseStatus.NotAttending,
	Api.EventRegistrationResponseStatus.NoResponse,
];

/**
 * The selection after "Include guests" changes. With "All recipients" checked it starts from every status, since one
 * with no registrants (Checked in, when only guests checked in) only gains people once guests are counted.
 */
export const getSelectionForIncludeGuests = (
	survey: TSurveyWithStats,
	selected: { id: string; representedObject: Api.EventRegistrationResponseStatus | null }[],
	includeGuests: boolean
): TFollowUpOption[] => {
	const wasAllSelected = selected.some(x => x.id === 'all');
	const statuses = wasAllSelected
		? allFollowUpStatuses
		: selected.map(x => x.representedObject).filter((x): x is Api.EventRegistrationResponseStatus => x !== null);
	const recounted = statuses
		.map(status => buildFollowUpStatusOption(survey, status, includeGuests))
		.filter(x => !x.disabled);
	return wasAllSelected ? [buildAllRecipientsOption(survey, includeGuests), ...recounted] : recounted;
};

type TSelectableOption = { disabled?: boolean; id: string };

// Toggling a single status never checks "All recipients", even when it is the only status with people in it.
export const getNextFollowUpSelection = <T extends TSelectableOption>(
	previous: T[],
	clicked: T[],
	options: T[]
): T[] => {
	const wasAllSelected = previous.some(x => x.id === 'all');
	const isAllSelected = clicked.some(x => x.id === 'all');
	if (isAllSelected && !wasAllSelected) {
		return options.filter(x => !x.disabled);
	}

	if (wasAllSelected && !isAllSelected) {
		return [];
	}

	return clicked.filter(x => x.id !== 'all');
};
